import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';

/**
 * Cosmetic, client-side only currency converter.
 * Converts the visible price text on the page using approximate fixed
 * exchange rates. It does not change the shop's real currency or what
 * gets charged at checkout — that's still controlled by Shopify markets
 * via the country selector.
 * @module currency-converter
 */

const STORAGE_KEY = 'theme:display-currency';

/** Approximate exchange rates relative to 1 USD. Update periodically. */
const RATES_PER_USD = {
  USD: 1,
  EUR: 0.92,
  GBP: 0.79,
  QAR: 3.64,
  AED: 3.67,
  SAR: 3.75,
  KWD: 0.31,
  BHD: 0.376,
  OMR: 0.385,
};

const PRICE_SELECTOR = [
  '.price',
  '.compare-at-price',
  'sale-price',
  'compare-at-price',
  '.money',
  '.cart-items__price',
  '.cart-items__price-unit',
  '.cart-totals__total-value',
  '.cart-totals__original-value',
  '.cart-discounts__value',
  '.cart-drawer__button-price',
  '.lou-item__price',
  '.curated__price',
  '.jayd-menu__slide-price',
  '.stl__price',
  '[data-cart-subtotal]',
  '[data-price-cents]',
].join(', ');

/**
 * @returns {string} The shop's real transactional currency (e.g. 'USD').
 */
function getShopCurrency() {
  return (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active) || 'USD';
}

/**
 * @param {string} value
 * @returns {boolean}
 */
function isSupportedCurrency(value) {
  return Object.prototype.hasOwnProperty.call(RATES_PER_USD, value);
}

/**
 * @returns {string|null} The saved display currency, if any and if supported.
 */
export function getSavedCurrency() {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return saved && isSupportedCurrency(saved) ? saved : null;
  } catch {
    return null;
  }
}

/**
 * @param {string} currency
 */
export function saveCurrency(currency) {
  try {
    window.localStorage.setItem(STORAGE_KEY, currency);
  } catch {
    // Ignore storage errors (private browsing, quota, etc).
  }
}

/**
 * Converts an amount, in minor units, from one currency to another using
 * the fixed approximate rates above.
 * @param {number} minorUnits
 * @param {string} fromCurrency
 * @param {string} toCurrency
 * @returns {number}
 */
const DECIMALS = { KWD: 3, BHD: 3, OMR: 3 };
const dec = (c) => DECIMALS[c] ?? 2;

function convert(minorUnits, fromCurrency, toCurrency) {
  const major = minorUnits / 10 ** dec(fromCurrency);
  const converted = (major / (RATES_PER_USD[fromCurrency] ?? 1)) * (RATES_PER_USD[toCurrency] ?? 1);
  return Math.round(converted * 10 ** dec(toCurrency));
}

/**
 * Finds the text node that actually holds the money string within a price
 * element. Some price components (e.g. <sale-price>) nest a visually
 * hidden `.sr-only` label span alongside the visible amount, and Shopify's
 * `money` filter itself wraps its output in a nested `<span class="money">`,
 * so we can't just overwrite the whole element's textContent without
 * destroying that structure — instead we walk down through any single
 * "money"/"price"-ish wrapper span to find the deepest element that holds
 * the amount, then return its last non-empty direct text node.
 * @param {HTMLElement} el
 * @returns {Text|null}
 */
function findMoneyTextNode(el) {
  let current = el;

  // Descend into nested wrapper spans (e.g. <span class="money">) as long
  // as they're the only element child and contain no sr-only label.
  while (true) {
    const elementChildren = Array.from(current.children).filter(
      (child) => !child.classList.contains('sr-only') && !child.hasAttribute('hidden')
    );
    if (elementChildren.length !== 1) break;
    current = elementChildren[0];
  }

  for (let i = current.childNodes.length - 1; i >= 0; i--) {
    const node = current.childNodes[i];
    if (node.nodeType === Node.TEXT_NODE && node.textContent && node.textContent.trim()) {
      return node;
    }
  }
  return null;
}

/**
 * Strips a leading or trailing currency ISO code (as added by Shopify's
 * `money_with_currency` filter, e.g. "120.00 QAR" or "QAR 120.00") so the
 * numeric part can be parsed, returning the amount text and whether a
 * currency code was found.
 * @param {string} text
 * @returns {{ amountText: string, hasCurrencyCode: boolean }}
 */
function splitCurrencyCode(text) {
  const trimmed = text.trim();

  const trailing = trimmed.match(/^(.*\S)\s+[A-Z]{3}$/);
  if (trailing && trailing[1]) return { amountText: trailing[1], hasCurrencyCode: true };

  const leading = trimmed.match(/^[A-Z]{3}\s+(\S.*)$/);
  if (leading && leading[1]) return { amountText: leading[1], hasCurrencyCode: true };

  return { amountText: trimmed, hasCurrencyCode: false };
}

/**
 * Converts and re-renders every price element on the page (or within a
 * given root) into the target display currency. Original text is cached
 * on the element so it can be restored/re-converted without drift.
 * @param {string} targetCurrency
 * @param {ParentNode} [root]
 */
export function applyDisplayCurrency(targetCurrency, root = document) {
  const shopCurrency = getShopCurrency();
  const scope = root instanceof Element && root.matches(PRICE_SELECTOR) ? root.parentElement ?? document : root;

  scope.querySelectorAll(PRICE_SELECTOR).forEach((el) => {
    if (!(el instanceof HTMLElement)) return;

    const ancestor = el.parentElement?.closest(PRICE_SELECTOR);
    if (ancestor && ancestor !== el) return;

    const textNode = findMoneyTextNode(el);
    if (!textNode) return;

    const centsAttr = el.getAttribute('data-price-cents');
    const hasCents = centsAttr != null && centsAttr !== '' && !Number.isNaN(Number(centsAttr));

    if (!hasCents && !el.dataset.baseCurrencyText) {
      el.dataset.baseCurrencyText = textNode.textContent ?? '';
    }

    const baseText = el.dataset.baseCurrencyText || textNode.textContent || '';

if (targetCurrency === shopCurrency) {
  if (hasCents) {
    const factor = 10 ** dec(shopCurrency);
    const roundedMinorUnits =
      Math.round(Number(centsAttr) / factor) * factor;

    textNode.textContent = formatMoney(
      roundedMinorUnits,
      '{{amount_no_decimals}} {{currency}}',
      shopCurrency
    );
  } else {
    const split = splitCurrencyCode(baseText);
    const minorUnits = convertMoneyToMinorUnits(
      split.amountText,
      shopCurrency
    );

    if (minorUnits !== null) {
      const factor = 10 ** dec(shopCurrency);
      const roundedMinorUnits =
        Math.round(minorUnits / factor) * factor;

      const suffix = split.hasCurrencyCode
        ? ` ${shopCurrency}`
        : '';

      textNode.textContent = formatMoney(
        roundedMinorUnits,
        `{{amount_no_decimals}}${suffix}`,
        shopCurrency
      );
    }
  }

  return;
}

    let minorUnits = null;
    let hasCurrencyCode = /\b[A-Z]{3}\b/.test(baseText);

    if (hasCents) {
      minorUnits = Number(centsAttr);
      hasCurrencyCode = true;
    } else {
      const split = splitCurrencyCode(baseText);
      hasCurrencyCode = split.hasCurrencyCode;
      minorUnits = convertMoneyToMinorUnits(split.amountText, shopCurrency);
    }

    if (minorUnits === null) return;
    const convertedMinorUnits = convert(minorUnits, shopCurrency, targetCurrency);

// Round to the nearest whole unit in the target currency.
const factor = 10 ** dec(targetCurrency);
const roundedMinorUnits =
  Math.round(convertedMinorUnits / factor) * factor;

const suffix = hasCurrencyCode ? ` ${targetCurrency}` : '';

textNode.textContent = formatMoney(
  roundedMinorUnits,
  `{{amount_no_decimals}}${suffix}`,
  targetCurrency
);
  });
}

/**
 * Applies the saved display currency (if one differs from the shop's
 * currency) to the whole document, and keeps re-applying it to any new
 * price elements that get added later (cart drawer updates, section
 * re-renders, etc).
 */
export function initDisplayCurrency() {
  const saved = getSavedCurrency();
  if (saved) applyDisplayCurrency(saved);

  // Undo the pre-paint hide (see the inline script in layout/theme.liquid)
  // now that any needed conversion has been applied.
  document.getElementById('currency-hide-until-ready')?.remove();

  const observer = new MutationObserver((mutations) => {
    const currency = getSavedCurrency();
    if (!currency) return;

    for (const mutation of mutations) {
      if (mutation.type === 'attributes' && mutation.target instanceof HTMLElement) {
        applyDisplayCurrency(currency, mutation.target);
        continue;
      }
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        if (node.matches(PRICE_SELECTOR)) applyDisplayCurrency(currency, node.parentElement ?? document);
        if (node.querySelector) applyDisplayCurrency(currency, node);
      });
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-price-cents'],
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDisplayCurrency);
} else {
  initDisplayCurrency();
}
